import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import styled from 'styled-components';
import LogoImage from '../../assets/litiumLogo.png?url';
import { AccountInfo } from '@azure/msal-browser';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  LoadingSpinner,
  Profile,
  QuestionAssistant,
} from '@tw/tw-component-library';
import { Button, Dropdown, Image } from '@/ui';
import { DynamicVersionModal } from '../common/DynamicVersionModal/DynamicVersionModal';
import {
  defaultHeader,
  guidedTutorials,
} from '../../mockData/landingPageMockData';
import packageJson from '../../../package.json';
import { useUserDetails } from '../../hooks/useUserDetails';
import { AuthContextProps } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useDebug } from '../../contexts/DebugContext';
import { DebugPanel, DebugOptionItem } from '../common/DebugPanel';
import { useWorkspaceErrorHandler } from '../../hooks/useWorkspaceErrorHandler';
import { CompactWorkspaceDirectoryList } from '../pages/workspaceDashboard/CompactWorkspaceDirectoryList';
import { useAppTheme } from '@/hooks/useAppTheme';
import { WorkspaceItem } from '@/contexts/WorkspaceContext';

interface HeaderLayoutProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
  activeAccount: AccountInfo | null;
  getAccessToken: AuthContextProps['getAccessToken'];
  login: () => Promise<void> | void;
  logout: () => Promise<void> | void;
  dropdown?: unknown;
  aiPolicyButton?: {
    label?: string;
    onClick?: () => void;
    buttonProps?: unknown;
  };
  profile?: unknown;
}

export const MainContent = styled.main`
  flex: 1;
`;

export const LayoutWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: ${({ theme }) => theme.colors.text.primary};
  gap: 2rem;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex: 1;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const LogoVersionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DropdownContainer = styled.div`
  min-width: 320px;
`;

const WorkspaceDirectoryTrigger = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  background: transparent;
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.45rem 0.8rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const WorkspaceDirectoryBackdrop = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 1200;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`;

const WorkspaceDirectoryPanel = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: min(420px, 92vw);
  background: ${({ theme }) => theme.colors.background};
  border-left: 1px solid ${({ theme }) => theme.colors.border.light};
  box-shadow: -8px 0 24px rgba(15, 23, 42, 0.12);
  z-index: 1201;
  padding: 0.85rem;
  overflow-y: auto;
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 0.22s ease;
`;

const WorkspaceDirectoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.65rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`;

const WorkspaceDirectoryTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

const BrandToggleButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.border.light};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary : 'transparent'};
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.45rem 0.8rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ProfileContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const VersionText = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.grays.gray1};
  font-weight: 500;
  letter-spacing: 0.5px;
  padding-right: 8px;
  cursor: pointer;
  transition: color 0.2s ease;
  user-select: none;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }

  &:active {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

// const UserDataErrorContainer = styled.div`
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   min-height: 60vh;
//   padding: 2rem;
// `;

// const UserDataErrorMessage = styled.p`
//   max-width: 720px;
//   margin: 0;
//   padding: 1rem 1.25rem;
//   border: 1px solid #f5c2c7;
//   border-radius: 8px;
//   background: #f8d7da;
//   color: #842029;
//   font-size: 1rem;
//   line-height: 1.5;
//   text-align: center;
// `;

// ErrorBanner component - kept for potential future use
// const ErrorBanner = ({ errors }: { errors: Record<string, string> }) => {
//   if (!errors || Object.keys(errors).length === 0) return null;
//
//   return (
//     <div
//       style={{
//         background: '#ffeaea',
//         color: '#b71c1c',
//         border: '1px solid #f44336',
//         borderRadius: '4px',
//         padding: '1rem',
//         marginBottom: '1.5rem',
//       }}
//       data-testid="form-error-banner"
//     >
//       <strong>There are errors in your form:</strong>
//       <ul style={{ margin: '0.5rem 0 0 1.5rem' }}>
//         {Object.entries(errors).map(([field, error]) => (
//           <li key={field}>
//             <span style={{ fontWeight: 500 }}>{field}:</span> {error}
//           </li>
//         ))}
//       </ul>
//     </div>
//   );
// };

export const HeaderLayout: React.FC<HeaderLayoutProps> = ({
  children,
  isAuthenticated,
  getAccessToken,
  login,
  logout,
}) => {
  const ProfileWithTheme = Profile as React.ComponentType<
    React.ComponentProps<typeof Profile> & {
      themeMode?: 'default' | 'compact';
      onThemeModeChange?: (mode: 'default' | 'compact') => void;
    }
  >;
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string }>();
  const { themeMode, isWinstonTaylor, setBrandTheme, setLayoutMode } =
    useAppTheme();
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [isWorkspaceSidebarOpen, setIsWorkspaceSidebarOpen] = useState(false);
  const hasReportedUserDataErrorRef = useRef(false);

  // Use WorkspaceContext for workspace data
  const {
    workspaceItemsWithPresence,
    dropdownGroups,
    currentWorkspaceId,
    setCurrentWorkspaceId,
  } = useWorkspace();
  const { canShowDebug, debug, setDebugFlag } = useDebug();
  const { handleError } = useWorkspaceErrorHandler();

  // Determine current workspace ID from route
  const routeWorkspaceId = useMemo(() => {
    // Check if we're in a workspace route
    if (location.pathname.startsWith('/workspace/') && params.id) {
      return params.id;
    }
    return null;
  }, [location.pathname, params.id]);
  const isAskHubRoute = useMemo(
    () => location.pathname.includes('/ask-hub'),
    [location.pathname],
  );

  // Update context when route changes
  // This ensures the dropdown reflects the current workspace when navigating
  // from landing page or any other route
  useEffect(() => {
    setCurrentWorkspaceId(routeWorkspaceId);
  }, [routeWorkspaceId, setCurrentWorkspaceId]);

  useEffect(() => {
    if (themeMode === 'compact' && isAskHubRoute && routeWorkspaceId) {
      navigate(`/workspace/${routeWorkspaceId}/ask-session`, { replace: true });
    }
  }, [themeMode, isAskHubRoute, routeWorkspaceId, navigate]);

  // ============= Helper Functions =============
  const formatUserName = (name: string): string => {
    return name.split(', ').reverse().join(' ');
  };

  const handleLogin = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleVersionClick = () => {
    setIsVersionModalOpen(true);
  };

  const handleVersionModalClose = () => {
    setIsVersionModalOpen(false);
  };

  const handleThemeModeChange = (mode: 'default' | 'compact') => {
    if (mode !== 'compact') {
      setIsWorkspaceSidebarOpen(false);
    }
    setLayoutMode(mode);
  };

  const handleBrandToggle = () => {
    setBrandTheme(isWinstonTaylor ? 'litium' : 'winstonTaylor');
  };

  const handleDirectoryCreateAskSession = useCallback(
    (item: WorkspaceItem) => {
      navigate(`/workspace/${item.id}/ask-session`);
      setIsWorkspaceSidebarOpen(false);
    },
    [navigate],
  );

  const handleDirectoryUploadDocument = useCallback(
    (item: WorkspaceItem) => {
      navigate(`/workspace/${item.id}/documents?upload=1`);
      setIsWorkspaceSidebarOpen(false);
    },
    [navigate],
  );

  const handleDirectoryRunTask = useCallback(
    (item: WorkspaceItem) => {
      navigate(`/workspace/${item.id}/prompt-library`);
      setIsWorkspaceSidebarOpen(false);
    },
    [navigate],
  );

  // ============= Data Collection =============

  const {
    userDetails,
    userImage,
    getUserDetails,
    getUserImageDetails,
    isLoading,
    error: userDetailsError,
  } = useUserDetails(getAccessToken as AuthContextProps['getAccessToken']);

  useEffect(() => {
    // Only fetch user details when authenticated
    if (isAuthenticated) {
      getUserDetails();
    }
  }, [isAuthenticated, getUserDetails]);

  useEffect(() => {
    // Only fetch user image after user details are available
    if (isAuthenticated && userDetails?.id) {
      getUserImageDetails();
    }
  }, [isAuthenticated, userDetails?.id, getUserImageDetails]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasReportedUserDataErrorRef.current = false;
      return;
    }

    // if (userDetailsError && !hasReportedUserDataErrorRef.current) {
    //   hasReportedUserDataErrorRef.current = true;
    //   handleError(
    //     new Error(userDetailsError),
    //     'An error has occured trying to recieve your user data from the server, please try again or reach out to a member of the Litium Team',
    //     undefined,
    //     false,
    //   );
    // }
  }, [isAuthenticated, userDetailsError, handleError]);

  const handleWorkspaceDirectoryItemClick = useCallback(
    (workspaceId: string) => {
      setCurrentWorkspaceId(workspaceId);
      navigate(`/workspace/${workspaceId}`);
      setIsWorkspaceSidebarOpen(false);
    },
    [navigate, setCurrentWorkspaceId],
  );

  const handleWorkspaceChange = useCallback(
    (value: string) => {
      navigate(`/workspace/${value}`);
    },
    [navigate],
  );

  const selectedWorkspaceValue = useMemo(() => {
    return currentWorkspaceId || defaultHeader.dropdown.selectedValue;
  }, [currentWorkspaceId]);

  // Build debug options for the dumb DebugPanel (only structure; panel doesn't know what they mean)
  const debugOptions: DebugOptionItem[] = useMemo(() => {
    if (!canShowDebug) return [];
    return [
      {
        id: 'enableToasts',
        label: 'SignalR toasts',
        description: 'User-facing popups (humanized messages)',
        value: debug.enableToasts,
        onChange: (v) => setDebugFlag('enableToasts', v),
      },
      {
        id: 'enableLogging',
        label: 'SignalR logs',
        description: 'Raw events in panel only (no popups)',
        value: debug.enableLogging,
        onChange: (v) => setDebugFlag('enableLogging', v),
      },
      {
        id: 'logHubHandlers',
        label: 'Log hub handlers',
        description: 'Console: registered handler summary',
        value: debug.logHubHandlers,
        onChange: (v) => setDebugFlag('logHubHandlers', v),
      },
    ];
  }, [
    canShowDebug,
    debug.enableToasts,
    debug.enableLogging,
    debug.logHubHandlers,
    setDebugFlag,
  ]);

  return (
    <LayoutWrapper>
      {isLoading && <LoadingSpinner variant="fullPage" />}
      <HeaderContainer>
        <LeftSection>
          <LogoVersionWrapper>
            <Image
              src={LogoImage}
              alt="Litium Logo"
              width={200}
              height={50}
              onClick={() => navigate('/')}
              style={{ cursor: 'pointer' }}
            />
            <VersionText
              onClick={handleVersionClick}
              title="Click to view version history"
            >
              v{packageJson.version}
            </VersionText>
          </LogoVersionWrapper>
          {isAuthenticated && (
            <>
              <DropdownContainer>
                {themeMode === 'compact' ? (
                  <WorkspaceDirectoryTrigger
                    type="button"
                    onClick={() => setIsWorkspaceSidebarOpen(true)}
                  >
                    Workspace Directory
                  </WorkspaceDirectoryTrigger>
                ) : (
                  <Dropdown
                    {...defaultHeader.dropdown}
                    groups={
                      dropdownGroups.length > 0
                        ? dropdownGroups
                        : defaultHeader.dropdown.groups
                    }
                    selectedValue={selectedWorkspaceValue}
                    onChange={handleWorkspaceChange}
                    height={600}
                  />
                )}
              </DropdownContainer>
            </>
          )}
        </LeftSection>
        <RightSection>
          {isAuthenticated ? (
            <>
              <BrandToggleButton
                type="button"
                $active={isWinstonTaylor}
                onClick={handleBrandToggle}
                aria-pressed={isWinstonTaylor}
                title={`Switch to ${
                  isWinstonTaylor ? 'Litium' : 'Winston Taylor'
                } theme`}
              >
                {isWinstonTaylor ? 'Winston Taylor' : 'Litium'}
              </BrandToggleButton>
              <ButtonContainer>
                {/* TODO: Look into moving this URL somewhere which could be potentially changed by other users. */}
                <Button
                  variant={'block'}
                  onClick={() => {
                    window.open(
                      'https://taylorwessing.sharepoint.com/sites/Intranet-Documents-TW/Shared%20Documents/TW%20UK/GenAI%20Policy.pdf',
                      '_blank',
                    );
                  }}
                >
                  TW Generated AI Use Policy
                </Button>
              </ButtonContainer>

              <ProfileContainer>
                {canShowDebug && (
                  <>
                    <Button
                      variant={'block'}
                      onClick={() => navigate('/signalr-test')}
                      style={{ marginRight: '8px' }}
                    >
                      SignalR Test
                    </Button>
                    <DebugPanel options={debugOptions} triggerLabel="Debug" />
                  </>
                )}
                <ProfileWithTheme
                  picture={userImage ?? undefined}
                  name={
                    userDetails?.name
                      ? formatUserName(userDetails.name)
                      : userDetails?.name
                  }
                  email={userDetails?.username}
                  variant="imageOnly"
                  size="small"
                  imageShape="square"
                  themeMode={themeMode}
                  onThemeModeChange={handleThemeModeChange}
                  onLogoutClick={handleLogout}
                />
              </ProfileContainer>
            </>
          ) : (
            <ButtonContainer>
              <Button
                variant="block"
                onClick={handleLogin}
                aria-label="Login to application"
              >
                Login
              </Button>
            </ButtonContainer>
          )}
        </RightSection>
      </HeaderContainer>

      <MainContent>
        {/* {isAuthenticated && userDetailsError ? (
          <UserDataErrorContainer>
            <UserDataErrorMessage>
              An error has occured trying to recieve your user data from the
              server, please try again or reach out to a member of the Litium
              Team
            </UserDataErrorMessage>
          </UserDataErrorContainer>
        ) : (
          children
        )} */}
        {children}
      </MainContent>

        {
        (themeMode === 'compact' && (
          <>
            <WorkspaceDirectoryBackdrop
              $open={isWorkspaceSidebarOpen}
              onClick={() => setIsWorkspaceSidebarOpen(false)}
            />
            <WorkspaceDirectoryPanel $open={isWorkspaceSidebarOpen}>
              <WorkspaceDirectoryHeader>
                <WorkspaceDirectoryTitle>
                  Workspace Directory
                </WorkspaceDirectoryTitle>
                <Button
                  variant="outline"
                  onClick={() => setIsWorkspaceSidebarOpen(false)}
                >
                  Close
                </Button>
              </WorkspaceDirectoryHeader>
              <CompactWorkspaceDirectoryList
                title="All workspaces"
                groups={workspaceItemsWithPresence}
                listLessTab="Private"
                enableSearch
                enablePagination
                enableTabs
                itemsPerPage={8}
                searchPlaceholder="Search workspaces..."
                onItemClick={(item) =>
                  handleWorkspaceDirectoryItemClick(item.id)
                }
                onCreateAskSession={handleDirectoryCreateAskSession}
                onUploadDocument={handleDirectoryUploadDocument}
                onRunTask={handleDirectoryRunTask}
              />
            </WorkspaceDirectoryPanel>
          </>
        ))}

      {/* Silent tutorial host: keeps guided overlay alive across non-landing pages. */}
      {location.pathname !== '/' && (
        <QuestionAssistant
          // @ts-expect-error guidedTutorials typing differs from the published QuestionAssistant typings.
          tutorials={guidedTutorials}
          hideMainUI
          hideTutorialPanel
          enableTutorialDiscovery={false}
          showTutorialSuggestions={false}
          enableHistory={false}
          enableKeywordExtraction={false}
        />
      )}

      <DynamicVersionModal
        isOpen={isVersionModalOpen}
        onClose={handleVersionModalClose}
      />
    </LayoutWrapper>
  );
};

// ============= PropTypes =============
HeaderLayout.displayName = 'HeaderLayout';
